import { isDefined, isTruthy } from 'notable-common';
import { z } from 'zod';
import { FlowDurationUnits } from '../../../duration/duration.js';
import { FlowProducedType } from '../../../flowRun/producedType.js';
import { BaseFlowBlockSchema, FlowBlockReturnTypeSchema } from '../block.js';
import { FlowConsumerOfPatientSchema, FlowConsumerOfProviderSchema, } from '../consumer.js';
import { FlowBlockType } from '../type.js';
import { FlowPatientDataStoreFilterConfigSchema, FlowPatientDataStoreSortConfigSchema, } from './filterAndSortConfig.js';
const stringTrimEmptyAsUndefined = () => z.string().transform((value) => {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
});
export var FlowPatientDataStoreEndpoint;
(function (FlowPatientDataStoreEndpoint) {
    FlowPatientDataStoreEndpoint["SurgicalHistories"] = "SurgicalHistories";
    FlowPatientDataStoreEndpoint["Medications"] = "Medications";
    FlowPatientDataStoreEndpoint["Encounters"] = "Encounters";
    FlowPatientDataStoreEndpoint["LabValues"] = "LabValues";
    FlowPatientDataStoreEndpoint["Conditions"] = "Conditions";
    FlowPatientDataStoreEndpoint["DocumentMetadata"] = "DocumentMetadata";
    FlowPatientDataStoreEndpoint["DocumentSearch"] = "DocumentSearch";
    FlowPatientDataStoreEndpoint["DocumentTranscriptions"] = "DocumentTranscriptions";
    FlowPatientDataStoreEndpoint["Practitioners"] = "Practitioners";
    FlowPatientDataStoreEndpoint["BookedAppointments"] = "BookedAppointments";
    FlowPatientDataStoreEndpoint["Orders"] = "Orders";
})(FlowPatientDataStoreEndpoint || (FlowPatientDataStoreEndpoint = {}));
// TODO: These should be generated from the schema
// and shared between the client and the server, but it's too hard now.
export const FLOW_PATIENT_DATA_STORE_SURGICAL_HISTORY_STATUS_OPTIONS = [
    'in-progress',
    'on-hold',
    'stopped',
    'completed',
];
export const FLOW_PATIENT_DATA_STORE_MEDICATION_STATUS_OPTIONS = [
    'active',
    'on-hold',
    'completed',
    'stopped',
];
export const FLOW_PATIENT_DATA_STORE_CONDITION_CLINICAL_STATUS_OPTIONS = [
    'active',
    'inactive',
    'recurrence',
    'relapse',
    'remission',
    'resolved',
    'unknown',
];
export const FLOW_PATIENT_DATA_STORE_CONDITION_VERIFICATION_STATUS_OPTIONS = [
    'confirmed',
    'differential',
    'entered-in-error',
    'provisional',
    'refuted',
    'unconfirmed',
];
export const FLOW_PATIENT_DATA_STORE_CONDITION_KIND_OPTIONS = [
    'ENCOUNTER_DIAGNOSIS',
    'PROBLEM_LIST',
];
export const FLOW_PATIENT_DATA_STORE_ORDER_STATUS_OPTIONS = [
    'active',
    'revoked',
    'completed',
];
export const FLOW_PATIENT_DATA_STORE_DURATION_UNIT_OPTIONS = [
    FlowDurationUnits.Years,
    FlowDurationUnits.Weeks,
    FlowDurationUnits.Days,
];
const DurationSchema = z.object({
    value: z.number().positive().int(),
    units: z.enum(FLOW_PATIENT_DATA_STORE_DURATION_UNIT_OPTIONS),
});
const ConsumerPdsDocumentMetadataSchema = z
    .object({
    sourceBlockName: z.string(),
})
    .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: FlowProducedType.PdsDocumentMetadata,
}));
const ConsumerPdsEncountersSchema = z
    .object({
    // Don't require consumesType in the input payload, we hardcode it
    sourceBlockName: z.string(),
})
    .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: FlowProducedType.PdsEncounters,
}));
const BasePatientDataStoreBlockConfigSchema = z.object({
    endpoint: z.enum(FlowPatientDataStoreEndpoint),
    patient: FlowConsumerOfPatientSchema({ isRequired: false }),
    baseFilters: z
        .object({
        filterConfig: FlowPatientDataStoreFilterConfigSchema.optional(),
        sortConfig: FlowPatientDataStoreSortConfigSchema.optional(),
        limit: z
            .union([z.number(), z.string()])
            .transform((value) => (isTruthy(value) ? Number(value) : undefined))
            .refine((value) => !isDefined(value) || value >= 0, 'Limit must be a non-negative number')
            .optional(),
    })
        .optional(),
});
export const FlowPatientDataStoreSurgicalHistoriesConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.SurgicalHistories),
    surgicalHistoryFilters: z
        .object({
        status: z.enum(FLOW_PATIENT_DATA_STORE_SURGICAL_HISTORY_STATUS_OPTIONS).optional(),
    })
        .optional(),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsSurgicalHistories),
});
export const FlowPatientDataStoreMedicationsConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.Medications),
    encounters: ConsumerPdsEncountersSchema.optional(),
    medicationFilters: z
        .object({
        status: z.enum(FLOW_PATIENT_DATA_STORE_MEDICATION_STATUS_OPTIONS).optional(),
    })
        .optional(),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsMedications),
});
export const FlowPatientDataStoreConditionConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.Conditions),
    encounters: ConsumerPdsEncountersSchema.optional(),
    conditionFilters: z
        .object({
        clinicalStatus: z
            .enum(FLOW_PATIENT_DATA_STORE_CONDITION_CLINICAL_STATUS_OPTIONS)
            .optional(),
        verificationStatus: z
            .enum(FLOW_PATIENT_DATA_STORE_CONDITION_VERIFICATION_STATUS_OPTIONS)
            .optional(),
        kind: z.enum(FLOW_PATIENT_DATA_STORE_CONDITION_KIND_OPTIONS).optional(),
        encounterLookbackDuration: DurationSchema.optional(),
    })
        .optional(),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsConditions),
});
export const FlowPatientDataStoreEncountersConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.Encounters),
    encounterFilters: z.object({ lookbackDuration: DurationSchema.optional() }).optional(),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsEncounters),
});
export const FlowPatientDataStoreLabValuesConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.LabValues),
    encounters: ConsumerPdsEncountersSchema.optional(),
    labValueFilters: z
        .object({
        lookbackDuration: DurationSchema.optional(),
    })
        .optional(),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsLabValues),
});
export const FlowPatientDataStoreDocumentMetadataConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.DocumentMetadata),
    encounters: ConsumerPdsEncountersSchema.optional(),
    documentMetadataFilters: z
        .object({
        documentType: stringTrimEmptyAsUndefined().optional(),
        documentTitle: stringTrimEmptyAsUndefined().optional(),
        lookbackDuration: DurationSchema.optional(),
    })
        .optional(),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsDocumentMetadata),
});
export const FlowPatientDataStoreDocumentTranscriptionsConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.DocumentTranscriptions),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsDocumentTranscriptions),
    documentMetadata: ConsumerPdsDocumentMetadataSchema,
});
export const FlowPatientDataStoreDocumentSearchConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.DocumentSearch),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsDocumentSearchResults),
    searchQuery: stringTrimEmptyAsUndefined(),
});
export const FlowPatientDataStorePractitionersConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.Practitioners),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsPractitioners),
    practitionerFilters: z
        .object({
        npi: stringTrimEmptyAsUndefined().optional(),
        provider: FlowConsumerOfProviderSchema.optional(),
    })
        .refine((data) => isDefined(data.provider) || isDefined(data.npi), 'Either provider or npi must be set for practitioner search'),
});
export const FlowPatientDataStoreBookedAppointmentsConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.BookedAppointments),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsBookedAppointments),
    bookedAppointmentFilters: z
        .object({
        // Use custom schema to allow negative values
        after: z
            .object({
            value: z.number().int(),
            units: z.enum(FLOW_PATIENT_DATA_STORE_DURATION_UNIT_OPTIONS),
        })
            .optional(),
    })
        .optional(),
});
export const FlowPatientDataStoreOrdersConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
    endpoint: z.literal(FlowPatientDataStoreEndpoint.Orders),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.PdsOrders),
    orderFilters: z.object({
        provider: FlowConsumerOfProviderSchema.optional(),
        encounters: ConsumerPdsEncountersSchema.optional(),
        status: z.enum(FLOW_PATIENT_DATA_STORE_ORDER_STATUS_OPTIONS).optional(),
    }),
});
export const FlowPatientDataStoreConfigSchema = z.discriminatedUnion('endpoint', [
    FlowPatientDataStoreSurgicalHistoriesConfigSchema,
    FlowPatientDataStoreMedicationsConfigSchema,
    FlowPatientDataStoreEncountersConfigSchema,
    FlowPatientDataStoreLabValuesConfigSchema,
    FlowPatientDataStoreConditionConfigSchema,
    FlowPatientDataStoreDocumentMetadataConfigSchema,
    FlowPatientDataStoreDocumentSearchConfigSchema,
    FlowPatientDataStoreDocumentTranscriptionsConfigSchema,
    FlowPatientDataStorePractitionersConfigSchema,
    FlowPatientDataStoreBookedAppointmentsConfigSchema,
    FlowPatientDataStoreOrdersConfigSchema,
]);
export const FlowPatientDataStoreBlockSchema = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.PatientDataStore),
    config: FlowPatientDataStoreConfigSchema,
});
//# sourceMappingURL=config.js.map